import {
  type ReviewEdgeFragment,
  useBrowser,
  useGetAverageReviewsForWorkerQuery,
  useGetReservationByIdQuery,
  useListReviewsQuery,
} from "@stepapp/core";
import { ReviewSortFields, SortDirection } from "@stepapp/core/apiTypes";
import {
  Avatar,
  Block,
  Divider,
  gradientPoint,
  Loader,
  palette,
  Rating,
  SvgIcon,
  spacing,
  TestimonialItem,
  Touchable,
  Typography,
  useBreakpoints,
} from "@stepapp/ui";
import { useLocalSearchParams } from "expo-router";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FlatList, StyleSheet } from "react-native";
import { ServiceNotice } from "../../../src/components";
import { useAppLinks } from "../../../src/hooks";
import WorkerBadge from "./WorkerBadge";

const keyExtractor = ({ cursor }: { cursor: string }) => cursor;

const OrderWorker = () => {
  const { reservationId = "", workerId = "" } = useLocalSearchParams<{
    reservationId: string;
    workerId: string;
  }>();
  const { md } = useBreakpoints();
  const { Method } = useAppLinks();

  const numColumns = md ? 3 : 1;
  const { t } = useTranslation();

  const { data: reviews, loading: reviewsLoading } = useListReviewsQuery({
    variables: {
      filter: { workerId: { eq: workerId } },
      sorting: { direction: SortDirection.Desc, field: ReviewSortFields.ReservationStartedAt },
    },
  });

  const { data: avgWorkerReviews } = useGetAverageReviewsForWorkerQuery({
    variables: { workerId },
    skip: !workerId,
  });

  const { data, loading } = useGetReservationByIdQuery({
    variables: { orderId: reservationId, isCustomerApp: true },
    skip: !reservationId,
  });

  const worker = data?.getReservationById?.workers?.find((worker) => worker?.id === workerId);

  const totalReviews = avgWorkerReviews?.getAverageReviewsForWorker?.total || 0;
  const finishedJobs = avgWorkerReviews?.getAverageReviewsForWorker?.completedCleanings || 0;
  const above15CompletedJobs = totalReviews > 15;
  const above5Reviews = totalReviews > 5;

  const hasReviews = !!reviews?.reviews?.edges?.length && !reviewsLoading;

  const openMethod = useBrowser(Method);

  const ListHeaderComponent = useCallback(
    () => (
      <Block marginTop={4} style={styles.container}>
        <Block style={styles.avatar}>
          <Avatar size={180} uri={worker?.avatar ?? ""} />
        </Block>

        <Block paddingTop={5} marginBottom={1}>
          <Block row justifySpace>
            <Typography variant="newH1">{worker?.name}</Typography>
            <Block justifyCenter style={styles.ratingBlock}>
              <Rating
                type="secondary"
                rating={avgWorkerReviews?.getAverageReviewsForWorker.average || 5}
                t={t}
              />
            </Block>
          </Block>
          {above15CompletedJobs && (
            <Typography type="secondary" variant="lightLabel" style={styles.finishedJobs}>
              {t("screens.predefinedProfile.finishedJobs", {
                count: finishedJobs,
              })}
            </Typography>
          )}
          {above5Reviews && (
            <Typography type="secondary" variant="lightLabel">
              {t("screens.predefinedProfile.customerReviews", {
                count: totalReviews,
              })}
            </Typography>
          )}
        </Block>
        <Divider color={palette.secondary.grey} />

        <Block paddingTop={3} paddingBottom={2}>
          <Typography variant="paragraph">{worker?.description}</Typography>
        </Block>
        <Typography variant="paragraph">{worker?.experience}</Typography>

        <Block paddingVertical={2}>
          <WorkerBadge type="training" name={worker?.name || ""} />
          <WorkerBadge type="safety" name={worker?.name || ""} />
        </Block>

        {hasReviews && (
          <>
            <Divider color={palette.secondary.grey} />

            <Block paddingTop={3}>
              <Typography variant="sectionTitle">
                {t("screens.predefinedProfile.lastReviews")}
              </Typography>
            </Block>

            <ServiceNotice type="review" containerStyles={styles.serviceNotice} />
          </>
        )}
      </Block>
    ),
    [
      avgWorkerReviews,
      totalReviews,
      finishedJobs,
      worker?.avatar,
      worker?.description,
      worker?.experience,
      above15CompletedJobs,
      above5Reviews,
      hasReviews,
      worker?.name,
      t,
    ],
  );

  const ListEmptyComponent = useCallback(
    () => (
      <Block alignCenter justifyCenter style={styles.emptyReviews}>
        <SvgIcon name="heart" size={30} gradientPoint={gradientPoint.pink} />
        <Block marginTop={1.5} paddingHorizontal={1}>
          <Typography center variant="bodyBold">
            {t("screens.predefinedProfile.weCareWhoComes")}
          </Typography>
        </Block>
        <Block marginVertical={1.5}>
          <Typography center variant="paragraph">
            {t("screens.predefinedProfile.learnMoreVerification")}
          </Typography>
        </Block>
        <Touchable onPress={openMethod}>
          <Typography center variant="bodyBold" type="primaryLink">
            {t("screens.predefinedProfile.readMore")}
          </Typography>
        </Touchable>
      </Block>
    ),
    [t, openMethod],
  );

  const renderItem = useCallback(
    ({ item }: { item: ReviewEdgeFragment }) => (
      <TestimonialItem
        postedAt={item?.node?.postedAt}
        key={item.node.id}
        containerStyle={styles.testimonialItem}
        review={item.node}
        t={t}
      />
    ),
    [t],
  );

  if (loading || !data?.getReservationById?.id) {
    return (
      <Block alignCenter justifyCenter flex={1}>
        <Loader />
      </Block>
    );
  }

  return (
    <FlatList
      ListHeaderComponent={ListHeaderComponent}
      ListEmptyComponent={ListEmptyComponent}
      key={numColumns}
      numColumns={numColumns}
      contentContainerStyle={styles.container}
      columnWrapperStyle={numColumns > 1 ? styles.columnWrapper : null}
      data={reviews?.reviews?.edges}
      keyExtractor={keyExtractor}
      renderItem={renderItem}
      onEndReachedThreshold={0.5}
      style={{ flex: 1 }}
    />
  );
};

export default OrderWorker;

const styles = StyleSheet.create({
  avatar: {
    margin: "auto",
  },
  columnWrapper: {
    gap: 32,
  },
  container: {
    backgroundColor: palette.common.white,
    borderTopLeftRadius: spacing.radius * 2,
    borderTopRightRadius: spacing.radius * 2,
  },
  ratingBlock: {
    backgroundColor: palette.primary.accent,
    height: spacing.unit * 4.5,
    borderRadius: spacing.unit,
    paddingHorizontal: spacing.unit,
    color: palette.common.white,
    boxShadow: "0px 2px 10px 0px rgba(0, 0, 84, 0.15)",
  },
  serviceNotice: {
    marginTop: spacing.unit * 3,
  },
  testimonialItem: {
    marginVertical: spacing.unit * 2,
    flex: 1,
    borderRadius: 8,
  },
  emptyReviews: {
    backgroundColor: "#F9E8EC",
    borderRadius: spacing.radius,
    padding: spacing.unit * 2.5,
    marginTop: spacing.unit * 3,
    marginBottom: spacing.unit * 10,
  },
  finishedJobs: {
    marginTop: spacing.unit,
    marginBottom: spacing.unit * 0.5,
  },
});
